import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from './Button';
import type { ButtonProps } from './Button';

interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'leftIcon' | 'children'> {
  /** Text der in die Zwischenablage kopiert wird. */
  value: string;
  /** Sichtbares Label. */
  children?: React.ReactNode;
  /** Aria-Label, wenn keine sichtbaren children. */
  ariaLabel?: string;
}

/**
 * Copy-To-Clipboard-Button mit kurzlebigem "kopiert"-Feedback.
 * Fällt auf document.execCommand zurück, wenn die Clipboard-API
 * nicht verfügbar ist (z.B. unsicherer Kontext).
 */
export function CopyButton({
  value,
  children,
  ariaLabel,
  size = 'sm',
  variant = 'ghost',
  ...rest
}: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Button
      {...rest}
      size={size}
      variant={variant}
      aria-label={ariaLabel ?? (typeof children === 'string' ? undefined : 'Kopieren')}
      onClick={() => void handleCopy()}
      leftIcon={
        copied ? (
          <Check className="size-3.5" aria-hidden="true" />
        ) : (
          <Copy className="size-3.5" aria-hidden="true" />
        )
      }
    >
      {children ?? (copied ? 'Kopiert' : 'Kopieren')}
    </Button>
  );
}
